import { useState, useRef, useEffect } from 'react';
import { ArrowUpTrayIcon, PhotoIcon, DocumentIcon, PresentationChartBarIcon } from '@heroicons/react/24/outline';
import {
  FilterSummary,
  EXPORT_WARNING_THRESHOLD,
  EXPORT_BLOCK_THRESHOLD,
} from '@/utils/exportView';
import { exportCanvasView } from '@/utils/exportCanvas';
import { exportAsPptx } from '@/utils/exportPptx';
import { useAppStore } from '@/stores/appStore';
import type { Initiative } from '@/types';

interface ExportDropdownProps {
  viewType: 'timeline' | 'list';
  viewContainerRef: React.RefObject<HTMLElement>;
  filterSummary: FilterSummary;
  initiativeCount: number;
  filteredInitiatives?: Initiative[];
}

export function ExportDropdown({
  viewType,
  viewContainerRef: _viewContainerRef,
  filterSummary,
  initiativeCount,
  filteredInitiatives,
}: ExportDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [pendingFormat, setPendingFormat] = useState<'png' | 'pdf' | 'pptx' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { products, quarters, classifications, statuses } = useAppStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (format: 'png' | 'pdf' | 'pptx') => {
    // Check if blocked
    if (initiativeCount > EXPORT_BLOCK_THRESHOLD) {
      alert(
        `Export too large (${initiativeCount} initiatives). Please apply filters to reduce the number of initiatives to ${EXPORT_BLOCK_THRESHOLD} or fewer, then export in batches.`
      );
      setIsOpen(false);
      return;
    }

    // Check if warning needed
    if (initiativeCount > EXPORT_WARNING_THRESHOLD) {
      setPendingFormat(format);
      setShowWarning(true);
      setIsOpen(false);
      return;
    }

    // Proceed with export
    await executeExport(format);
  };

  const executeExport = async (format: 'png' | 'pdf' | 'pptx') => {
    setIsExporting(true);
    setIsOpen(false);
    setShowWarning(false);

    // Allow React to render the "Exporting..." overlay before heavy sync work
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      if (format === 'pptx') {
        const initiatives = filteredInitiatives || [];
        const result = await exportAsPptx({
          viewType,
          filterSummary,
          initiatives,
          products,
          quarters,
          classifications,
          statuses,
        });
        if (!result.success) {
          alert(`Export failed: ${result.error}`);
        }
      } else {
        const initiatives = filteredInitiatives || [];
        const result = await exportCanvasView({
          viewType,
          format,
          filterSummary,
          initiatives,
          products,
          quarters,
          classifications,
          statuses,
        });
        if (!result.success) {
          alert(`Export failed: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
      setPendingFormat(null);
    }
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isExporting}
          className={`flex items-center gap-1 p-2 text-text-secondary hover:text-text-primary hover:bg-surface-hover rounded-md transition-colors ${
            isExporting ? 'opacity-50 cursor-wait' : ''
          }`}
          title="Export View"
        >
          {isExporting ? (
            <div className="w-5 h-5 border-2 border-text-secondary border-t-transparent rounded-full animate-spin" />
          ) : (
            <ArrowUpTrayIcon className="w-5 h-5" />
          )}
        </button>

        {isOpen && (
          <div className="absolute top-full right-0 mt-1 w-44 bg-background-elevated border border-border-default rounded-md shadow-lg z-50">
            <div className="py-1">
              <button
                onClick={() => handleExport('png')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors"
              >
                <PhotoIcon className="w-4 h-4 text-text-secondary" />
                <span>Export PNG</span>
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors"
              >
                <DocumentIcon className="w-4 h-4 text-text-secondary" />
                <span>Export PDF</span>
              </button>
              <button
                onClick={() => handleExport('pptx')}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors"
              >
                <PresentationChartBarIcon className="w-4 h-4 text-text-secondary" />
                <span>Export PowerPoint</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Exporting Overlay */}
      {isExporting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl px-10 py-8 flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-base font-semibold text-gray-900">Exporting...</p>
            <p className="text-sm text-gray-500">Please wait while your file is generated</p>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md mx-4 p-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Export Warning</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Your current view contains <strong>{initiativeCount}</strong> initiatives.
                  Large exports may be slow or fail.
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  Recommendation: Apply filters to reduce the number of initiatives and export in
                  batches.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowWarning(false);
                  setPendingFormat(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => pendingFormat && executeExport(pendingFormat)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Export Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
